import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAccount, emptyProfile, type ProfileFields } from "@/hooks/use-account";

export const Route = createFileRoute("/_authenticated/settings")({
  head: () => ({
    meta: [
      { title: "Profile settings | Finendra" },
      {
        name: "description",
        content: "Update your Finendra account details, contact preferences and notifications.",
      },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: SettingsPage,
});

const TIMEZONES = [
  "America/New_York",
  "America/Chicago",
  "America/Denver",
  "America/Los_Angeles",
  "Europe/London",
  "Asia/Kolkata",
  "Asia/Manila",
  "UTC",
];

const inputClass =
  "mt-2 w-full rounded-xl border border-brand-navy/15 bg-background px-4 py-3 text-brand-navy outline-none focus:border-brand-accent";
const labelClass = "text-xs font-bold uppercase tracking-widest text-brand-navy/50";

function SettingsPage() {
  const account = useAccount();
  const navigate = useNavigate();
  const [form, setForm] = useState<ProfileFields>(emptyProfile);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setForm(account.profile);
  }, [account.profile]);

  const dirty = useMemo(
    () => JSON.stringify(form) !== JSON.stringify(account.profile),
    [form, account.profile],
  );

  function set<K extends keyof ProfileFields>(key: K, value: ProfileFields[K]) {
    setForm((prev) => ({ ...prev, [key]: value }));
    setStatus(null);
  }

  async function onSubmit(event: React.FormEvent) {
    event.preventDefault();
    if (!account.userId) return;
    if (form.phone && !/^[0-9+()\-.\s]{7,20}$/.test(form.phone.trim())) {
      setError("Enter a valid phone number.");
      return;
    }
    setSaving(true);
    setStatus(null);
    setError(null);

    const payload = {
      display_name: form.displayName.trim() || null,
      company: form.company.trim() || null,
      phone: form.phone.trim() || null,
      timezone: form.timezone || null,
      bio: form.bio.trim() || null,
      ...(account.isAdmin
        ? {
            job_title: form.jobTitle.trim() || null,
            notify_new_inquiries: form.notifyNewInquiries,
            notify_new_quotes: form.notifyNewQuotes,
          }
        : {}),
    };

    const { error: updateError } = await supabase
      .from("profiles")
      .update(payload)
      .eq("id", account.userId);

    if (updateError) {
      setError("We couldn't save your changes. Please try again.");
    } else {
      await account.refresh();
      setStatus("Changes saved.");
    }
    setSaving(false);
  }

  async function signOut() {
    await supabase.auth.signOut();
    await navigate({ to: "/auth", replace: true });
  }

  if (account.loading) {
    return (
      <main className="flex min-h-screen items-center justify-center px-6">
        <p className="text-brand-navy/60">Loading your settings…</p>
      </main>
    );
  }

  return (
    <main className="min-h-screen px-6 pt-24 md:pt-32 pb-28">
      <div className="mx-auto max-w-3xl">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="text-xs font-bold uppercase tracking-widest text-brand-accent">
              {account.isAdmin ? "Admin account" : "Member account"}
            </p>
            <h1 className="mt-3 font-display text-4xl font-bold text-brand-navy">
              Profile settings
            </h1>
            <p className="mt-2 text-sm text-brand-navy/60">
              Update how your name and contact details appear across quotes and proposals.
            </p>
          </div>
          <div className="flex flex-wrap gap-3">
            <Link
              to={account.isAdmin ? "/admin" : "/dashboard"}
              className="rounded-xl border border-brand-navy/15 px-5 py-3 text-sm font-bold text-brand-navy hover:border-brand-accent hover:text-brand-accent"
            >
              Back to dashboard
            </Link>
            <button
              type="button"
              onClick={signOut}
              className="rounded-xl bg-brand-navy px-5 py-3 text-sm font-bold text-background hover:bg-brand-accent"
            >
              Sign out
            </button>
          </div>
        </div>

        <form onSubmit={onSubmit} className="mt-10 space-y-8">
          <section className="rounded-3xl border border-brand-navy/10 bg-background p-8">
            <h2 className="font-display text-2xl font-bold text-brand-navy">Account</h2>
            <div className="mt-6 grid gap-5 sm:grid-cols-2">
              <div>
                <label htmlFor="displayName" className={labelClass}>
                  Display name
                </label>
                <input
                  id="displayName"
                  value={form.displayName}
                  onChange={(e) => set("displayName", e.target.value.slice(0, 80))}
                  placeholder="How should we address you?"
                  className={inputClass}
                />
              </div>
              <div>
                <span className={labelClass}>Email</span>
                <p className="mt-2 rounded-xl bg-brand-surface px-4 py-3 text-brand-navy/70">
                  {account.email ?? "—"}
                </p>
              </div>
              <div>
                <label htmlFor="phone" className={labelClass}>
                  Phone
                </label>
                <input
                  id="phone"
                  value={form.phone}
                  onChange={(e) => set("phone", e.target.value.slice(0, 20))}
                  placeholder="+1 555 123 4567"
                  className={inputClass}
                />
              </div>
              <div>
                <label htmlFor="timezone" className={labelClass}>
                  Time zone
                </label>
                <select
                  id="timezone"
                  value={form.timezone}
                  onChange={(e) => set("timezone", e.target.value)}
                  className={inputClass}
                >
                  <option value="">Not set</option>
                  {TIMEZONES.map((tz) => (
                    <option key={tz} value={tz}>
                      {tz}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </section>

          {account.isAdmin ? (
            <section className="rounded-3xl border border-brand-navy/10 bg-background p-8">
              <h2 className="font-display text-2xl font-bold text-brand-navy">Admin details</h2>
              <p className="mt-2 text-sm text-brand-navy/60">
                Shown internally on the admin console and used for staff notifications.
              </p>
              <div className="mt-6 space-y-5">
                <div>
                  <label htmlFor="jobTitle" className={labelClass}>
                    Job title
                  </label>
                  <input
                    id="jobTitle"
                    value={form.jobTitle}
                    onChange={(e) => set("jobTitle", e.target.value.slice(0, 80))}
                    placeholder="Engagement Manager"
                    className={inputClass}
                  />
                </div>
                <label className="flex items-center gap-3 text-sm font-medium text-brand-navy">
                  <input
                    type="checkbox"
                    checked={form.notifyNewInquiries}
                    onChange={(e) => set("notifyNewInquiries", e.target.checked)}
                    className="h-4 w-4 accent-[color:var(--color-brand-accent,#0f766e)]"
                  />
                  Notify me about new contact inquiries
                </label>
                <label className="flex items-center gap-3 text-sm font-medium text-brand-navy">
                  <input
                    type="checkbox"
                    checked={form.notifyNewQuotes}
                    onChange={(e) => set("notifyNewQuotes", e.target.checked)}
                    className="h-4 w-4 accent-[color:var(--color-brand-accent,#0f766e)]"
                  />
                  Notify me when a new quote is saved
                </label>
              </div>
            </section>
          ) : (
            <section className="rounded-3xl border border-brand-navy/10 bg-background p-8">
              <h2 className="font-display text-2xl font-bold text-brand-navy">Business details</h2>
              <p className="mt-2 text-sm text-brand-navy/60">
                Used to pre-fill your quotes and proposals.
              </p>
              <div className="mt-6 space-y-5">
                <div>
                  <label htmlFor="company" className={labelClass}>
                    Company
                  </label>
                  <input
                    id="company"
                    value={form.company}
                    onChange={(e) => set("company", e.target.value.slice(0, 100))}
                    placeholder="Acme Inc."
                    className={inputClass}
                  />
                </div>
                <div>
                  <label htmlFor="bio" className={labelClass}>
                    About your finance function
                  </label>
                  <textarea
                    id="bio"
                    rows={4}
                    value={form.bio}
                    onChange={(e) => set("bio", e.target.value.slice(0, 500))}
                    placeholder="Team size, systems you use, current pain points…"
                    className={inputClass}
                  />
                  <p className="mt-1 text-xs text-brand-navy/50">{form.bio.length}/500</p>
                </div>
              </div>
            </section>
          )}

          {error && (
            <p role="alert" className="text-sm font-medium text-destructive">
              {error}
            </p>
          )}
          {status && <p className="text-sm font-medium text-brand-accent">{status}</p>}

          <div className="flex flex-wrap items-center gap-4">
            <button
              type="submit"
              disabled={saving || !dirty}
              className="rounded-xl bg-brand-navy px-6 py-3 text-sm font-bold text-background hover:bg-brand-accent disabled:opacity-50"
            >
              {saving ? "Saving…" : "Save changes"}
            </button>
            <button
              type="button"
              onClick={() => {
                setForm(account.profile);
                setError(null);
                setStatus(null);
              }}
              disabled={!dirty || saving}
              className="rounded-xl border border-brand-navy/15 px-6 py-3 text-sm font-bold text-brand-navy hover:border-brand-accent hover:text-brand-accent disabled:opacity-40"
            >
              Discard
            </button>
            {dirty && (
              <span className="text-xs font-semibold uppercase tracking-widest text-brand-navy/50">
                Unsaved changes
              </span>
            )}
          </div>
        </form>
      </div>
    </main>
  );
}
